import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Stack,
  Paper,
  Chip,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  AccountBalance,
  CloudQueue,
  Speed,
  Analytics,
  Security,
  Assessment,
  TrendingUp,
  CheckCircle,
  ArrowForward,
  Person,
  Business,
  DataUsage,
  Payment,
  Shield
} from '@mui/icons-material';
import { Link } from 'react-router-dom';

const BankingFinancial = () => {
  const caseStudies = [
    {
      title: 'Digital Banking Platform Modernization',
      description: 'A major bank modernized their core banking systems, improving customer experience and reducing transaction processing time by 70%.',
      link: '#'
    },
    {
      title: 'Risk Management & Compliance',
      description: 'A financial institution implemented advanced risk analytics and compliance monitoring, achieving 100% regulatory compliance and reducing risk exposure by 40%.',
      link: '#'
    },
    {
      title: 'Payment Solutions Integration',
      description: 'A regional bank integrated modern payment solutions, enabling real-time transactions and improving customer satisfaction by 50%.',
      link: '#'
    }
  ];

  const keyStats = [
    { value: '70%', description: 'reduction in transaction processing time through digital modernization.' },
    { value: '100%', description: 'regulatory compliance achieved through advanced risk management.' },
    { value: '50%', description: 'improvement in customer satisfaction through modern payment solutions.' }
  ];

  const services = [
    {
      icon: <AccountBalance sx={{ fontSize: 40, color: '#1976d2' }} />,
      title: 'Digital Banking',
      description: 'Modern digital banking platforms with seamless customer experiences.'
    },
    {
      icon: <Security sx={{ fontSize: 40, color: '#1976d2' }} />,
      title: 'Risk Management',
      description: 'Advanced risk assessment and compliance monitoring solutions.'
    },
    {
      icon: <Payment sx={{ fontSize: 40, color: '#1976d2' }} />,
      title: 'Payment Solutions',
      description: 'Secure and efficient payment processing and transaction management.'
    },
    {
      icon: <Analytics sx={{ fontSize: 40, color: '#1976d2' }} />,
      title: 'Financial Analytics',
      description: 'Data-driven insights for better decision making and customer service.'
    }
  ];

  const expert = {
    name: 'Robert Chen',
    title: 'Vice President, Banking & Financial Markets',
    company: 'Scherstellen Technologie',
    image: '/api/placeholder/150/150',
    qa: [
      {
        question: 'How are financial institutions modernizing IT to exceed customer expectations?',
        answer: 'Financial institutions are implementing digital-first strategies, cloud-native platforms, and AI-powered services that provide personalized experiences, faster transactions, and enhanced security to meet evolving customer demands.'
      },
      {
        question: 'What role does cybersecurity play in financial services?',
        answer: 'Cybersecurity is critical in financial services, with institutions implementing multi-layered security approaches including encryption, fraud detection, and continuous monitoring to protect customer data and maintain trust.'
      },
      {
        question: 'How are banks leveraging data analytics for better customer service?',
        answer: 'Banks are using advanced analytics to understand customer behavior, personalize services, detect fraud, and make data-driven decisions that improve customer satisfaction and operational efficiency.'
      }
    ]
  };

  return (
    <>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          py: 12,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontWeight: 'bold',
                color: '#1a1a1a',
                mb: 3,
                lineHeight: 1.2
              }}
            >
              Banking & Financial Markets
            </Typography>
            <Typography
              variant="h3"
              sx={{
                color: '#1976d2',
                mb: 2,
                fontWeight: 'bold'
              }}
            >
              Helping financial institutions modernize IT
            </Typography>
            <Typography
              variant="h5"
              sx={{
                color: '#666',
                mb: 4,
                lineHeight: 1.6,
                maxWidth: '800px',
                mx: 'auto'
              }}
            >
              Exceed customer expectations with innovative financial technology solutions
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Overview Section */}
      <Box sx={{ py: 10, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 'bold',
                  color: '#1a1a1a',
                  mb: 3
                }}
              >
                Overview
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: '#666',
                  mb: 4,
                  lineHeight: 1.6
                }}
              >
                Scherstellen Technologie helps financial institutions modernize their IT infrastructure to exceed customer expectations. We enable digital transformation, secure transactions, and data-driven insights that drive growth and customer loyalty in the financial services industry.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  height: 400,
                  bgcolor: '#f5f5f5',
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Typography variant="h6" color="text.secondary">
                  Banking & Financial Technology Solutions
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Case Studies Section */}
      <Box sx={{ py: 10, bgcolor: '#f8f9fa' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            sx={{
              fontWeight: 'bold',
              color: '#1a1a1a',
              mb: 6,
              textAlign: 'center'
            }}
          >
            Success Stories
          </Typography>
          <Grid container spacing={4}>
            {caseStudies.map((study, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 'bold',
                        color: '#1a1a1a',
                        mb: 2
                      }}
                    >
                      {study.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: '#666',
                        mb: 3,
                        lineHeight: 1.6
                      }}
                    >
                      {study.description}
                    </Typography>
                    <Button
                      variant="outlined"
                      endIcon={<ArrowForward />}
                      sx={{
                        borderColor: '#1976d2',
                        color: '#1976d2',
                        '&:hover': {
                          borderColor: '#1565c0',
                          color: '#1565c0',
                          bgcolor: 'rgba(25, 118, 210, 0.04)'
                        }
                      }}
                    >
                      Learn more
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Key Statistics */}
      <Box sx={{ py: 10, bgcolor: '#1976d2', color: 'white' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            sx={{
              fontWeight: 'bold',
              mb: 6,
              textAlign: 'center'
            }}
          >
            3 things to know
          </Typography>
          <Grid container spacing={4}>
            {keyStats.map((stat, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 'bold',
                      mb: 2
                    }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography variant="h6" sx={{ opacity: 0.9 }}>
                    {stat.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Services Section */}
      <Box sx={{ py: 10, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            sx={{
              fontWeight: 'bold',
              color: '#1a1a1a',
              mb: 6,
              textAlign: 'center'
            }}
          >
            Our Banking & Financial Solutions
          </Typography>
          <Grid container spacing={4}>
            {services.map((service, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      {service.icon}
                      <Typography
                        variant="h5"
                        sx={{
                          ml: 2,
                          fontWeight: 'bold',
                          color: '#1a1a1a'
                        }}
                      >
                        {service.title}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body1"
                      sx={{
                        color: '#666',
                        lineHeight: 1.6
                      }}
                    >
                      {service.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Expert Q&A Section */}
      <Box sx={{ py: 10, bgcolor: '#f8f9fa' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            sx={{
              fontWeight: 'bold',
              color: '#1a1a1a',
              mb: 6,
              textAlign: 'center'
            }}
          >
            Industry Experts
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: '#666',
              mb: 6,
              textAlign: 'center'
            }}
          >
            Our experts, your challenges
          </Typography>
          
          <Grid container spacing={6}>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Avatar
                  src={expert.image}
                  sx={{
                    width: 150,
                    height: 150,
                    mx: 'auto',
                    mb: 3
                  }}
                />
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 'bold',
                    color: '#1a1a1a',
                    mb: 1
                  }}
                >
                  {expert.name}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color: '#1976d2',
                    mb: 1
                  }}
                >
                  {expert.title}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#666'
                  }}
                >
                  {expert.company}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={8}>
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 'bold',
                    color: '#1a1a1a',
                    mb: 4
                  }}
                >
                  Q&A with Scherstellen Technologie banking expert, {expert.name}
                </Typography>
                
                {expert.qa.map((qa, index) => (
                  <Box key={index} sx={{ mb: 4 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 'bold',
                        color: '#1976d2',
                        mb: 2
                      }}
                    >
                      Q: {qa.question}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: '#666',
                        lineHeight: 1.6,
                        mb: 3
                      }}
                    >
                      A: {qa.answer}
                    </Typography>
                    {index < expert.qa.length - 1 && <Divider sx={{ my: 2 }} />}
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ py: 10, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Paper
            elevation={0}
            sx={{
              p: 6,
              textAlign: 'center',
              bgcolor: '#1976d2',
              color: 'white',
              borderRadius: 2
            }}
          >
            <Typography
              variant="h3"
              sx={{
                fontWeight: 'bold',
                mb: 3
              }}
            >
              Connect with an expert to discuss your IT Transformation
            </Typography>
            <Typography
              variant="h5"
              sx={{
                mb: 4,
                opacity: 0.9
              }}
            >
              Let's help you modernize and transform your financial operations
            </Typography>
            <Button
              variant="contained"
              size="large"
              sx={{
                bgcolor: 'white',
                color: '#1976d2',
                px: 6,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                '&:hover': {
                  bgcolor: '#f5f5f5'
                }
              }}
            >
              Contact Us
            </Button>
          </Paper>
        </Container>
      </Box>
    </>
  );
};

export default BankingFinancial;
